import { getLang, getLocale } from '../i18n/lang';

// Every formatter follows the widget language (i18n/lang.ts): de-DE with
// "Mrd. €" suffixes, or en-GB with "€…bn" prefixes.

export function formatThreeDigits(value: number): string {
  if (value === 0) return '0';
  const locale = getLocale();
  const absValue = Math.abs(value);
  if (absValue >= 100) return value.toLocaleString(locale, { maximumFractionDigits: 0 });
  if (absValue >= 10) return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A plain integer in the widget locale ("9.000" / "9,000"). */
export function formatInteger(value: number): string {
  return Math.round(value).toLocaleString(getLocale());
}

const VOLUME_UNITS = {
  de: {
    bn: (n: string) => `${n} Mrd. €`,
    m: (n: string) => `${n} Mio. €`,
    k: (n: string) => `${n} Tsd. €`,
    one: (n: string) => `${n} €`,
  },
  en: {
    bn: (n: string) => `€ ${n} bn`,
    m: (n: string) => `€ ${n} m`,
    k: (n: string) => `€ ${n} k`,
    one: (n: string) => `€ ${n}`,
  },
};

export function formatInvestmentVolume(value: number | null): string {
  if (value == null) return '-';

  const u = VOLUME_UNITS[getLang()];
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return u.bn(formatThreeDigits(value / 1_000_000_000));
  }
  if (abs >= 1_000_000) {
    return u.m(formatThreeDigits(value / 1_000_000));
  }
  if (abs >= 1_000) {
    return u.k(formatThreeDigits(value / 1_000));
  }
  return u.one(formatThreeDigits(value));
}

/** Job counts in the locale's thousands grouping ("9.000" / "9,000"). Null or
    0 means "no figure curated", not "zero jobs" — returns null so the caller
    can omit the field entirely rather than print a placeholder. */
export function formatJobs(value: number | null): string | null {
  if (value == null || value <= 0) return null;
  const n = formatInteger(value);
  if (getLang() === 'en') return `${n} ${Math.round(value) === 1 ? 'job' : 'jobs'}`;
  return `${n} Arbeitsplätze`;
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** "5. August 2026" / "5 August 2026" — long form for the publication/update line. */
export function formatDateLong(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(getLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Convert raw EUR value to millions for consistent bubble sizing */
export function volumeToMillions(value: number | null): number {
  if (value == null) return 0;
  return value / 1_000_000;
}

// Every timestamp in the time-range filter is UTC-midnight aligned: the API
// serves meta.dateRange as a bare YYYY-MM-DD (which parses as UTC midnight)
// and App.tsx sends the filter back out through toISOString(). These two
// convert to and from a <input type="date"> value in the same UTC frame —
// parsing a typed date as *local* midnight would land on the previous day
// everywhere east of Greenwich, filtering one day off what the user typed.

/** Timestamp → the YYYY-MM-DD a date field expects. */
export function timestampToDateInput(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

/** A date field's YYYY-MM-DD → timestamp, or null while the entry is incomplete. */
export function dateInputToTimestamp(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ts = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(ts)) return null;
  // Date.parse rolls 2026-02-31 over into March; a round-trip rejects that.
  return timestampToDateInput(ts) === value ? ts : null;
}
